import * as ngCore from '@angular/core'
import * as ngForms from '@angular/forms'
import * as ents from '@attain-apps/entities'

import * as state from '../../../../../state'
import * as switcher from '../switcher'

import * as knowledgeBaseRetrievalDialog from './knowledge-base-retrieval-dialog'

const DEFAULT_MAX_MESSAGE_HEIGHT_PX = 200
const DEFAULT_LINE_HEIGHT_PX = 24

/**
 * Message input component with support for text entry and file attachments
 */
@ngCore.Component({
  imports: [
    ngForms.FormsModule,
    switcher.Component,
    knowledgeBaseRetrievalDialog.Component,
  ],
  selector: 'atn-assistant-message-input',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** Accepted file types for the file input (used in template) */
  public readonly acceptedFileTypes =
    state.fileMgmt.models.FileRequest.supportedUploadExtensions
      .map(extension => `.${extension}`)
      .join(',')

  /** @see {@link ents.core.agent.Display} */
  public readonly assistantDisplaysSg = ngCore.input<ents.core.agent.Display[]>(
    [],
    { alias: 'assistants' },
  )
  /** Selected agent ID. */
  public readonly selectedAgentIdSg = ngCore.model<
    ents.core.agent.Agent.Id | undefined
  >(undefined, { alias: 'selectedAgentId' })
  /** Whether assistant selection is disabled */
  public readonly assistantSelectionDisabledSg = ngCore.input<boolean>(false, {
    alias: 'assistantSelectionDisabled',
  })
  /**
   * Whether file attachments are allowed
   */
  public allowFileAttachmentsSg = ngCore.input<boolean>(false, {
    alias: 'allowFileAttachments',
  })

  /** Whether the input is disabled */
  public readonly disabledSg = ngCore.input<boolean>(false, {
    alias: 'disabled',
  })

  /** Textarea element reference (required) */
  private readonly _textareaRef =
    ngCore.viewChild.required<ngCore.ElementRef<HTMLTextAreaElement>>(
      'textarea',
    )

  /** File input element reference */
  private readonly _fileInputRef =
    ngCore.viewChild<ngCore.ElementRef<HTMLInputElement>>('fileInput')

  /** Current content of the textarea */
  public readonly contentSg = ngCore.model<string>('', { alias: 'content' })

  /** Currently selected files */
  public readonly selectedFilesSg = ngCore.signal<File[]>([])

  /** User-facing validation feedback for files rejected before upload. */
  public readonly fileErrorSg = ngCore.signal<string | undefined>(undefined)

  /** Whether a file is being dragged over the input */
  public readonly isDraggingOverSg = ngCore.signal<boolean>(false)

  /**
   * DMS records currently selected to authorize for knowledge-base
   * retrieval on the next submitted message. Ephemeral: not persisted.
   */
  public readonly knowledgeBaseRetrievalRecordsSg = ngCore.signal<
    readonly ents.core.agent.KnowledgeBase.Retrieval.Record[] | undefined
  >(undefined)

  /**
   * Knowledge-base retrieval records supplied externally (e.g. by a
   * mosaic action opening the slideout with session context). When
   * present, these take precedence over user-typed records and the
   * retrieval dialog switches to a read-only view.
   */
  public readonly externalKnowledgeBaseRetrievalRecordsSg = ngCore.input<
    readonly ents.core.agent.KnowledgeBase.Retrieval.Record[] | undefined
  >(undefined, { alias: 'externalKnowledgeBaseRetrievalRecords' })

  /** Whether the knowledge-base retrieval context is externally controlled */
  public readonly isKnowledgeBaseRetrievalLockedSg = ngCore.computed(
    () => this.externalKnowledgeBaseRetrievalRecordsSg() !== undefined,
  )

  /** The records that will actually be submitted with the next message */
  public readonly effectiveKnowledgeBaseRetrievalRecordsSg = ngCore.computed(
    () =>
      this.externalKnowledgeBaseRetrievalRecordsSg() ??
      this.knowledgeBaseRetrievalRecordsSg(),
  )

  /** Whether the knowledge-base retrieval dialog is currently open */
  public readonly showKnowledgeBaseRetrievalDialogSg =
    ngCore.signal<boolean>(false)

  /** Emits message content on submit */
  public readonly submit = ngCore.output<{
    content: string
    files: File[]
    knowledgeBaseRetrieval?: readonly ents.core.agent.KnowledgeBase.Retrieval.Record[]
  }>()

  /** Signal for calculated textarea height (in px) */
  public readonly textareaHeightPxSg = ngCore.signal(DEFAULT_LINE_HEIGHT_PX)

  /** Whether textarea content exceeds max height */
  public readonly isScrollableSg = ngCore.computed(
    () => this.textareaHeightPxSg() >= DEFAULT_MAX_MESSAGE_HEIGHT_PX,
  )

  constructor() {
    // Effect that recalculates textarea height whenever content changes
    ngCore.effect(() => {
      const content = this.contentSg()
      // Schedule after DOM update
      requestAnimationFrame(() => {
        const textarea = this._textareaRef().nativeElement
        if (!textarea) {
          return
        }

        // Calculate new textarea height
        if (!content) {
          textarea.style.height = 'auto'
        }

        const newHeight = Math.min(
          Math.max(textarea.scrollHeight, DEFAULT_LINE_HEIGHT_PX),
          DEFAULT_MAX_MESSAGE_HEIGHT_PX,
        )

        // Set new height
        this.textareaHeightPxSg.set(newHeight)
      })
    })
  }

  /** Handle Enter key: submit and clear if Shift not pressed */
  public onEnterHandler(event: Event): void {
    const keyboardEvent = event as KeyboardEvent
    if (!keyboardEvent.shiftKey) {
      event.preventDefault()
      const message = this.contentSg().trim()
      if (message || this.selectedFilesSg().length > 0) {
        this.submit.emit({
          content: message,
          files: this.selectedFilesSg(),
          knowledgeBaseRetrieval:
            this.effectiveKnowledgeBaseRetrievalRecordsSg(),
        })
        this.contentSg.set('')
        this.selectedFilesSg.set([])
      }
    }
  }

  /** Handle submit button click */
  public onSubmitClick(): void {
    const message = this.contentSg().trim()
    if (message || this.selectedFilesSg().length > 0) {
      this.submit.emit({
        content: message,
        files: this.selectedFilesSg(),
        knowledgeBaseRetrieval: this.effectiveKnowledgeBaseRetrievalRecordsSg(),
      })
      this.contentSg.set('')
      this.selectedFilesSg.set([])
    }
  }

  /** Trigger file input click */
  public onAttachFileClick(): void {
    this._fileInputRef()?.nativeElement.click()
  }

  /** Handle file selection from input */
  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement
    if (input.files && input.files.length > 0) {
      this._addFiles(Array.from(input.files))
      input.value = '' // Reset input to allow selecting same file again
    }
  }

  /** Remove a file from selection */
  public removeFile(index: number): void {
    this.selectedFilesSg.update(files => files.filter((_, i) => i !== index))
  }

  /** Opens the knowledge-base retrieval dialog */
  public onKnowledgeBaseRetrievalClick(): void {
    this.showKnowledgeBaseRetrievalDialogSg.set(true)
  }

  /** Handles the knowledge-base retrieval dialog being saved */
  public onKnowledgeBaseRetrievalSave(
    records:
      | readonly ents.core.agent.KnowledgeBase.Retrieval.Record[]
      | undefined,
  ): void {
    if (this.isKnowledgeBaseRetrievalLockedSg()) {
      this.showKnowledgeBaseRetrievalDialogSg.set(false)
      return
    }
    this.knowledgeBaseRetrievalRecordsSg.set(records)
    this.showKnowledgeBaseRetrievalDialogSg.set(false)
  }

  /** Handles the knowledge-base retrieval dialog being cancelled */
  public onKnowledgeBaseRetrievalCancel(): void {
    this.showKnowledgeBaseRetrievalDialogSg.set(false)
  }

  /** Handle drag over event */
  public onDragOver(event: DragEvent): void {
    event.preventDefault()
    event.stopPropagation()
    if (this.allowFileAttachmentsSg() && !this.disabledSg()) {
      this.isDraggingOverSg.set(true)
    }
  }

  /** Handle drag leave event */
  public onDragLeave(event: DragEvent): void {
    event.preventDefault()
    event.stopPropagation()
    this.isDraggingOverSg.set(false)
  }

  /** Handle file drop event */
  public onDrop(event: DragEvent): void {
    event.preventDefault()
    event.stopPropagation()
    this.isDraggingOverSg.set(false)

    if (!this.allowFileAttachmentsSg() || this.disabledSg()) {
      return
    }

    const files = event.dataTransfer?.files
    if (files && files.length > 0) {
      this._addFiles(Array.from(files))
    }
  }

  /** Validates and adds attachments before the upload flow can begin. */
  private _addFiles(files: readonly File[]): void {
    const supportedFiles = files.filter(file =>
      state.fileMgmt.models.FileRequest.isSupportedUploadFile(file),
    )
    const unsupportedFiles = files.filter(
      file => !supportedFiles.includes(file),
    )

    this.fileErrorSg.set(
      unsupportedFiles.length > 0
        ? `${unsupportedFiles.map(file => `"${file.name}"`).join(', ')} cannot be attached. Supported file types: ${state.fileMgmt.models.FileRequest.supportedUploadExtensions.map(extension => `.${extension}`).join(', ')}.`
        : undefined,
    )

    if (supportedFiles.length > 0) {
      this.selectedFilesSg.update(existingFiles => [
        ...existingFiles,
        ...supportedFiles,
      ])
    }
  }
}
